import React, { useState } from 'react';
import { Check, ChevronDown, Copy, ExternalLink } from 'lucide-react';

import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import LogoLockup from '@dashboard/components/LogoLockup';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import {
  STRIPE_TEST_CARDS_URL,
  defaultTestCard,
  findTestCard,
  testCardGroups,
} from '@dashboard/lib/stripeTestCards';

function CopyButton({ label, value }) {
  const [isCopied, setIsCopied] = useState(false);

  return (
    <CopyToClipboard
      text={value}
      onCopy={() => {
        setIsCopied(true);

        window.setTimeout(() => {
          setIsCopied(false);
        }, 1000);
      }}
    >
      <button
        type="button"
        aria-label={label}
        className="rounded-md p-1.5 text-white/60 transition-colors hover:bg-white/10 dark:hover:bg-background/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        {isCopied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </button>
    </CopyToClipboard>
  );
}

function CardChip() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 40 30"
      className="h-7 w-9 text-white/60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect x="1" y="1" width="38" height="28" rx="5" />
      <path d="M1 11h11M1 19h11M28 11h11M28 19h11M12 1v28M28 1v28M12 15h16" />
    </svg>
  );
}

function CardDetail({ label, value, copyLabel }) {
  return (
    <div className="flex items-end gap-2">
      <div>
        <div className="text-[0.625rem] font-medium uppercase tracking-widest text-white/60">
          {label}
        </div>
        <div className="text-base font-medium tabular-nums tracking-widest">{value}</div>
      </div>

      <CopyButton label={copyLabel} value={value} />
    </div>
  );
}

export default function TestCard() {
  const [cardId, setCardId] = useState(defaultTestCard.id);
  const card = findTestCard(cardId);

  return (
    <div className="relative mx-auto flex aspect-[1.84/1] w-full max-w-md min-h-44 flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-neutral-700 via-neutral-900 to-black p-5 text-white shadow-lg">
      <div className="flex items-start justify-between gap-4">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 dark:border-border/15 bg-white/10 dark:bg-background/10 px-3 py-1 text-xs font-medium text-white/90 transition-colors hover:bg-white/15 dark:hover:bg-background/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <span className="sr-only">Test card: </span>
              {card.name} · {card.outcome}
              <ChevronDown className="size-3.5" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="w-96">
            <DropdownMenuRadioGroup value={cardId} onValueChange={setCardId}>
              {testCardGroups.map(group => (
                <React.Fragment key={group.label}>
                  <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </DropdownMenuLabel>

                  {group.cards.map(groupCard => (
                    <DropdownMenuRadioItem key={groupCard.id} value={groupCard.id}>
                      <span className="flex w-full items-center justify-between gap-6">
                        <span className="whitespace-nowrap">{groupCard.name}</span>
                        <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                          {groupCard.number}
                        </span>
                      </span>
                    </DropdownMenuRadioItem>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenuRadioGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <a href={STRIPE_TEST_CARDS_URL} target="_blank" rel="noreferrer">
                All Stripe test cards
                <ExternalLink className="ml-1.5 size-3.5" />
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="shrink-0 text-white">
          <LogoLockup height={13} />
        </span>
      </div>

      <CardChip />

      <div className="flex items-center justify-between gap-4">
        <span className="text-xl font-medium tabular-nums tracking-[0.16em]">{card.number}</span>

        <CopyButton label="Copy card number" value={card.number} />
      </div>

      <div className="flex items-end gap-8">
        <CardDetail label="Valid thru" value={card.expiry} copyLabel="Copy expiry date" />
        <CardDetail label="CVC" value={card.cvc} copyLabel="Copy CVC" />
      </div>
    </div>
  );
}
